import { isIOS, isSafari } from 'react-device-detect';
import PQueue from 'p-queue';

import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';
import { getContext } from 'configs/context';

import { ConcurrentOperation, getConcurrencyLevel } from 'pages/Batch/utils';
import { IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import loadingImage from 'utils/images/loadingImage';
import { apiUploadUrl } from 'configs/constants';
import { AiService } from 'services/aiService';

const SDK_BOOTSTRAP_TIMEOUT_MS = 10_000;

const waitForSdkHeaders = (): Promise<void> =>
  new Promise(resolve => {
    if (Object.keys(getContext().utils.api.getHeaders() ?? {}).length > 0) {
      resolve();
      return;
    }
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    setTimeout(finish, SDK_BOOTSTRAP_TIMEOUT_MS);
    getContext().handlers.onHeadersUpdate(finish);
  });

export class UploadPhoto {
  private concurrency = getConcurrencyLevel(ConcurrentOperation.UPLOAD);

  private queue: PQueue;

  constructor() {
    this.queue = new PQueue({ concurrency: this.concurrency });
  }

  private isWebpByMime(blob: Blob): boolean {
    return blob.type === 'image/webp' && !(isSafari || isIOS);
  }

  public async upload(blob: Blob, type = 'web-editor', fileType = IMAGE_MIME_TYPES.WEBP): Promise<string | null | void> {
    this.queue.concurrency = getConcurrencyLevel(ConcurrentOperation.UPLOAD);

    const image = await loadingImage(URL.createObjectURL(blob));
    const resultBlob =
      fileType === IMAGE_MIME_TYPES.WEBP && this.isWebpByMime(blob)
        ? blob
        : await AiService.downloadImage({
            image,
            width: image.naturalWidth,
            height: image.naturalHeight,
            fileType,
            sourceMime: blob.type,
          });

    const fileName = `${Date.now().toString(36)}${resultBlob?.type}`;

    if (!resultBlob) {
      errorLogger.trigger(`Can't convert image to ${fileType}`, BUGSNAG_STATUSES.ERROR);
      return null;
    }

    return this.queue.add(async () => {
      const file = new File([resultBlob], fileName, {
        type: resultBlob.type,
        lastModified: Date.now(),
      });

      const data = new FormData();
      data.append('type', type);
      data.append('file', file);

      errorLogger.leaveBreadcrumb('Uploading file', { size: file.size, type, fileName }, 'log');

      await waitForSdkHeaders();

      const uploadUrl = `${apiUploadUrl}/files`;
      const maxRetries = 2;

      async function attemptUpload(retries: number): Promise<string | null> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30_000);

        try {
          const response = await getContext().utils.api.fetch(uploadUrl, {
            method: 'POST',
            body: data,
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          const { result } = await response.json();

          if (!result?.download_url) return '';

          return result.download_url as string;
        } catch (err) {
          clearTimeout(timeoutId);

          if ((err as Error).name === 'AbortError') {
            if (retries < maxRetries - 1) {
              await new Promise(r => setTimeout(r, 200));
              return attemptUpload(retries + 1);
            }
            errorLogger.trigger('Max retries reached. Upload failed due to repeated timeouts.');
            return null;
          }
          const msg = (err as Error).message ?? '';
          if (msg.includes('bootstrap')) {
            errorLogger.trigger(`SDK not bootstrapped during upload: ${msg}`, BUGSNAG_STATUSES.ERROR);
          } else {
            errorLogger.trigger(err as Error, BUGSNAG_STATUSES.ERROR);
          }
          return null;
        }
      }

      return attemptUpload(0);
    });
  }
}

export default new UploadPhoto();
